import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useMount, useUnmount } from 'react-use';

import { type ShortUrlResponse } from '@air/api/types';

import { BoardlessConfigViewStore } from '~/classes/BoardlessConfigViewStore';
import { PublicBoardlessConfigViewObserver } from '~/components/PublicBoardlessConfigViewObserver';
import { type DefaultBoardlessConfigViewsParams } from '~/constants/configViews';
import {
  resetConfigViewsAction,
  setCardSizeAction,
  setCurrentViewTypeAction,
  setSavedConfigurableViewsAction,
  setThumbnailPreferenceAction,
} from '~/store/configViews/actions';

export const GENERAL_PAGE_ID = 'general';

export type PublicBoardlessConfigViewsParams = {
  pageId: string;
  defaultSort?: DefaultBoardlessConfigViewsParams['defaultSort'];
  presentationDefaultView?: NonNullable<ShortUrlResponse['presentationSettings']>['defaultView'];
};

/**
 * This hook loads boardless page config view from local storage for public contexts
 * If there is no config view saved for the page, it will create a default config view
 * It returns an element that can be used to observe the changes in the config view and save them to local storage
 */
export const usePublicBoardlessConfigView = ({
  defaultSort,
  pageId,
  presentationDefaultView,
}: PublicBoardlessConfigViewsParams) => {
  const dispatch = useDispatch();

  useMount(() => {
    const configViewsForPage = BoardlessConfigViewStore.getInstance().getSavedViewsForPage(pageId, defaultSort);
    dispatch(
      setSavedConfigurableViewsAction({
        savedConfigurableViews: configViewsForPage,
        defaultMetadataFieldsVisible: presentationDefaultView?.metadataFieldsVisible,
      }),
    );

    if (presentationDefaultView?.viewType) {
      dispatch(setCurrentViewTypeAction({ viewTypeName: presentationDefaultView.viewType.name }));
    }

    if (presentationDefaultView?.cardSize) {
      dispatch(setCardSizeAction({ cardSize: presentationDefaultView.cardSize }));
    }

    if (presentationDefaultView?.thumbnailStyle) {
      dispatch(setThumbnailPreferenceAction({ thumbnailPreference: presentationDefaultView.thumbnailStyle }));
    }
  });

  useUnmount(() => {
    dispatch(resetConfigViewsAction());
  });

  const PublicBoardlessConfigObserver = useMemo(
    () => <PublicBoardlessConfigViewObserver pageId={pageId} defaultSort={defaultSort} />,
    [pageId, defaultSort],
  );

  return {
    PublicBoardlessConfigObserver,
  };
};
